import React, { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { Label } from 'reactstrap'

const EXTENSION_BY_MIME_TYPE = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/bmp': 'bmp',
    'image/tiff': 'tiff',
}

function extensionForMimeType(mimeType) {
    return EXTENSION_BY_MIME_TYPE[mimeType] ?? 'png'
}

/**
 * One drag-drop/click/paste image upload slot (react-dropzone handles all
 * three through the same onDrop), always replacing whatever's currently
 * in this slot. Generic over what it uploads to and what it displays -
 * used for both a sign type's Dimension Drawing/Overview images and a
 * company's logo, so the dropzone/thumbnail/download-link UI isn't
 * duplicated between them.
 * @param {string} label - shown as this slot's title and passed to
 *      buildName
 * @param {function(file, name, onProgress): Promise} uploadFn - performs
 *      the actual upload; onProgress receives axios's upload-progress event
 * @param {function(mimeType): string} buildName - builds the uploaded
 *      file's display name (e.g. "Drawing Images/{given_id}/{label}.jpg")
 * @param {?string} imageUrl - null if the caller already knows nothing's
 *      uploaded yet (e.g. the per-sign images, backed by a DB row).
 * @param {boolean} [mayNotExist] - true when imageUrl is a guessed/stable
 *      URL that might 404 (e.g. a company logo, which has no DB row to
 *      check existence against first - the S3 key is always the same
 *      whether or not anything's been uploaded there yet). When true,
 *      the thumbnail is attempted and falls back to the empty/upload
 *      state on a load error instead of assuming imageUrl is valid.
 * @param {?string} downloadName - filename for the download link's
 *      download attribute; ignored if imageUrl is null
 * @param {function()} onUploaded
 * @param {function(error)} showError
 */
function DrawingImageUploadView({
    label, uploadFn, buildName, imageUrl, mayNotExist, downloadName, onUploaded, showError,
}) {
    const [progress, setProgress] = useState(0)
    const [uploading, setUploading] = useState(false)
    const [imageMissing, setImageMissing] = useState(false)
    // The S3 key behind a company logo never changes on replace, so the
    // browser would otherwise keep showing a stale cached image after a
    // successful upload - bump this to force a fresh <img> load.
    const [cacheBust, setCacheBust] = useState(0)

    const onDrop = useCallback((acceptedFiles) => {
        const file = acceptedFiles[0]
        if (!file) {
            return
        }
        const name = buildName(file.type)
        setUploading(true)
        setProgress(0)
        uploadFn(file, name, (event) => {
            setProgress(Math.round((100 * event.loaded) / event.total))
        }).then(
            () => {
                setUploading(false)
                setImageMissing(false)
                setCacheBust((c) => c + 1)
                onUploaded()
            },
            (error) => {
                setUploading(false)
                showError(error)
            },
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadFn, buildName])

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'image/*': [] },
        multiple: false,
    })

    const showImage = imageUrl && !imageMissing
    const displayUrl = imageUrl && `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}v=${cacheBust}`

    return (
        <div>
            <Label style={{ fontWeight: 'bold' }}>{label}</Label>
            {showImage && <div style={{ marginBottom: '4px' }}>
                <img
                    src={displayUrl}
                    alt={label}
                    style={{ maxHeight: '150px', display: 'block', marginBottom: '4px' }}
                    onError={mayNotExist ? () => setImageMissing(true) : undefined}
                    />
                <a href={displayUrl} download={downloadName}>Download</a>
            </div>}
            <div {...getRootProps()} style={{
                border: '2px dashed var(--bs-border-color)',
                borderRadius: '4px',
                padding: '12px',
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: isDragActive ? 'var(--bs-tertiary-bg)' : undefined,
            }}>
                <input {...getInputProps()} />
                {uploading
                    ? <span>Uploading... {progress}%</span>
                    : <span>
                        {showImage ? 'Drop, click, or paste to replace' :
                            'Drop image, click to browse, or paste'}
                    </span>}
            </div>
        </div>
    )
}

export { extensionForMimeType }
export default DrawingImageUploadView
